// ============================================================================
// Error Messages for Facility / Equipment Management Epic
// ============================================================================

/**
 * Error messages for Facility Management Epic (equipment creation)
 * Contains validation errors and API error messages
 */
export const EQUIPMENT_ERROR_MESSAGES = {
  // Validation errors
  REQUIRED: 'To pole jest wymagane', // This field is required
  DESCRIPTION_TOO_LONG: 'Opis może zawierać maksymalnie 500 znaków', // Description max 500 characters
  UPLOAD_INCOMPLETE: 'Błąd serwera. Spróbuj ponownie.', // Upload failed or still in progress at submit time

  // Category
  CATEGORY_REQUIRED: 'Kategoria jest wymagana', // Category is required

  // Serial number
  SERIAL_NUMBER_INVALID_CHARS: 'Dozwolone: litery, cyfry, spacje i myślniki', // Alphanumeric, spaces, hyphens only
  SERIAL_NUMBER_TOO_LONG: 'Numer seryjny może mieć maksymalnie 200 znaków', // Max 200 characters
  SERIAL_NUMBER_DUPLICATE: 'Ten numer seryjny już istnieje', // Serial number already exists

  // Date of production
  DATE_FUTURE: 'Data produkcji nie może być datą przyszłą', // Cannot be a future date

  // Model name
  MODEL_NAME_INVALID_CHARS: 'Dozwolone: litery, cyfry, spacje i myślniki', // Alphanumeric, spaces, hyphens only
  MODEL_NAME_TOO_LONG: 'Nazwa modelu może mieć maksymalnie 200 znaków', // Max 200 characters

  // API errors
  DEFAULT_ERROR: 'Wystąpił błąd podczas dodawania sprzętu', // An error occurred while adding equipment
  NETWORK_ERROR: 'Coś poszło nie tak. Spróbuj ponownie.', // Something went wrong. Try again.
  UNEXPECTED_ERROR: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.', // An unexpected error occurred. Try again.
  ACCESS_DENIED:
    'Your permissions have been revoked. You no longer have access to add equipment. Please contact your administrator.',

  // File upload errors (used when backend returns filename validation errors)
  FILE_TOO_LARGE: 'Plik jest za duży (max 5MB)', // File is too large (max 5MB)
  INVALID_FILE_TYPE: 'Nieprawidłowy typ pliku', // Invalid file type
  MAX_DOCS_EXCEEDED: 'Można przesłać maksymalnie 2 dokumenty', // Maximum 2 documents allowed
  INVALID_FILENAME:
    'Nazwy plików nie mogą zawierać "/", "\\", "?", "&", "%". Zmień nazwę pliku i spróbuj ponownie.', // File names cannot contain special characters
} as const;

export const FACILITY_CREATE_ERROR_MESSAGES = {
  ACCESS_DENIED:
    'Twoje uprawnienia zostały cofnięte. Nie masz już dostępu do tworzenia tej placówki. Skontaktuj się z administratorem.',
  ACCESS_DENIED_TOOLTIP: "You don't have permission to add a facility.",
  LIMIT_REACHED_TOOLTIP: (limit: number) =>
    `Możesz dodać maksymalnie ${limit} placówek dla tego klienta.`,
  LIMIT_REACHED_SNACKBAR: (limit?: number) =>
    limit !== undefined
      ? `Osiągnięto limit ${limit} placówek dla tego planu subskrypcji.`
      : 'Osiągnięto limit placówek dla tego planu subskrypcji.',
  HARD_LIMIT_TOOLTIP: 'Maksymalnie 10 placówek dozwolonych na klienta.',
} as const;

import plMessages from '../constants/plMessages.json';

export const FACILITY_EDIT_ERROR_MESSAGES = {
  ACCESS_DENIED_TOOLTIP: plMessages.editFacility.errors.accessDeniedTooltip,
  // Any raw 403 from the backend (which may contain verbose AVP messages) must be
  // replaced with this generic string. Normalization happens in editFacilityApiService.ts.
  ACCESS_DENIED: plMessages.editFacility.errors.accessDenied,
  DEFAULT_ERROR: plMessages.editFacility.errors.defaultError,
  NETWORK_ERROR: plMessages.editFacility.errors.networkError,
  UNEXPECTED_ERROR: plMessages.editFacility.errors.unexpectedError,
  LOAD_ERROR: plMessages.editFacility.errors.loadError,
} as const;

export const EMPLOYEE_ERROR_MESSAGES = {
  REQUIRED: 'To pole jest wymagane',
  INVALID_EMAIL: 'Nieprawidłowy adres email',
  INVALID_PHONE: 'Nieprawidłowy numer telefonu',
  EMAIL_ALREADY_EXISTS: 'Ten adres e-mail jest już zarejestrowany',
  EMAIL_CONFLICT: (email: string) =>
    `Email "${email}" already exists for this employee`,
  LIMIT_REACHED: (limit?: number) =>
    limit !== undefined
      ? `Osiągnięto limit ${limit} pracowników dla tego planu subskrypcji`
      : 'Osiągnięto limit pracowników dla tego planu subskrypcji',
  DEFAULT_ERROR: 'Wystąpił błąd podczas dodawania pracownika',
  ACCESS_DENIED: 'Nie masz uprawnień do wykonania tej czynności',
  NETWORK_ERROR: 'Coś poszło nie tak. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
} as const;

export const FACILITY_VIEW_ERROR_MESSAGES = {
  DEFAULT_ERROR: 'Wystąpił błąd podczas ładowania placówki',
  NETWORK_ERROR: 'Coś poszło nie tak. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
  ACCESS_DENIED: "You don't have sufficient permission to perform this action",
} as const;
